import React, { useState, useEffect, FC, JSX } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  shouldShowProfileCompletionModal,
  markProfileAsCompleted,
} from "../../utils/profileUtils";
import Button from "../../components/ui/Button";
import { AgentProfileFormData } from "../../types/components.d";
import { LocalStorageUser } from "../../types/localStorage.d";
// import removed: AgentProfileModal

const AgentDashboard: FC = (): JSX.Element => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState<AgentProfileFormData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // removed: showProfileModal state

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        if (user && user.role === "Agent") {
          // Check if profile is complete using the proper utility
          const shouldShowModal = shouldShowProfileCompletionModal(user);
          const savedData = localStorage.getItem("agentProfileData");

          // removed: modal toggles

          if (savedData) {
            try {
              const parsedData: AgentProfileFormData = JSON.parse(savedData);
              setProfileData(parsedData);
            } catch (error: any) {
              setError("Error loading profile data");
            }
          }
        }
      } catch (error: any) {
        setError("Error loading dashboard data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const cards = [
    { label: "Assigned companies", value: 0 },
    { label: "Open requisitions", value: 0 },
    { label: "Candidates this week", value: 0 },
    { label: "Offers accepted", value: 0 },
  ];

  const tasks: { id: number; title: string; company: string; due: string }[] = [
    // { id: 1, title: 'Shortlist candidates for Frontend Engineer', company: 'BrightHub', due: 'Today' },
    // { id: 2, title: 'Schedule screening calls', company: 'Dhoondh', due: 'Tomorrow' },
    // { id: 3, title: 'Share interview feedback', company: 'Healthly', due: 'Fri' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-lightGreen mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agent dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading dashboard
            </h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="rounded-2xl p-5 sm:p-6 bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-primary-black">
              Welcome, Agent
            </h1>
            <p className="text-primary-black/80 text-sm sm:text-base">
              Track applications and manage companies from one view.
            </p>
          </div>
          {/* <div className="flex flex-wrap gap-2">
            <Link to="/agent/onboarding">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto bg-white text-primary-black border-primary-black hover:bg-primary-black hover:text-white font-semibold"
              >
                Complete onboarding
              </Button>
            </Link>
            <Link to="/agent/profile/complete">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto bg-white text-primary-black border-primary-black hover:bg-primary-black hover:text-white font-semibold"
              >
                Complete Profile
              </Button>
            </Link>
            <Link to="/agent/manage">
              <Button className="w-full sm:w-auto bg-primary-black text-white hover:bg-gray-800 font-semibold">
                Open workspace
              </Button>
            </Link>
          </div> */}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            className="rounded-xl bg-white p-4 border border-gray-100"
          >
            <div className="text-xs sm:text-sm text-gray-600">{c.label}</div>
            <div className="mt-1 text-lg sm:text-xl font-bold text-primary-black">
              {c.value}
            </div>
          </div>
        ))}
      </div>

      {tasks.length > 0 && (
        <div className="rounded-2xl bg-white p-5 sm:p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg font-semibold text-primary-black">
              Today’s tasks
            </h2>
            <Link to="/agent/manage">
              <Button variant="ghost" size="sm">
                View all
              </Button>
            </Link>
          </div>
          <div className="space-y-3">
            {tasks.map((t) => (
              <div
                key={t.id}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between rounded-xl border p-3 gap-2"
              >
                <div className="min-w-0">
                  <div className="font-medium text-primary-black truncate">
                    {t.title}
                  </div>
                  <div className="text-sm text-gray-600">{t.company}</div>
                </div>
                <div className="text-sm text-gray-700">Due: {t.due}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Data Display - For Testing */}
      {profileData && (
        <div className="rounded-2xl bg-white p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Profile Data
            </h3>
          </div>
          <pre className="text-xs bg-gray-50 p-3 rounded border overflow-auto">
            {JSON.stringify(profileData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default AgentDashboard;
